import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { createUser, getUser, updateUser } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PageInfo, UserDetail } from "@/types/api";

const createSchema = z.object({
  username: z
    .string()
    .min(3, "Минимум 3 символа")
    .max(64)
    .regex(/^[a-zA-Z0-9_.-]+$/, "Только буквы/цифры/._-"),
  password: z.string().min(8, "Минимум 8 символов"),
  is_admin: z.boolean(),
  permissions: z.array(z.string()),
});

const updateSchema = z.object({
  password: z.union([z.string().min(8, "Минимум 8 символов"), z.literal("")]).optional(),
  is_admin: z.boolean(),
  is_active: z.boolean(),
  permissions: z.array(z.string()),
});

type CreateValues = z.infer<typeof createSchema>;
type UpdateValues = z.infer<typeof updateSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingUserId: number | null;
  availablePages: PageInfo[];
  onSaved: () => void;
}

export function UserFormDialog({
  open,
  onOpenChange,
  editingUserId,
  availablePages,
  onSaved,
}: Props): React.ReactElement {
  const isEdit = editingUserId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Редактировать пользователя" : "Создать пользователя"}
          </DialogTitle>
        </DialogHeader>
        {isEdit ? (
          <EditForm
            userId={editingUserId}
            availablePages={availablePages}
            onSaved={() => {
              onOpenChange(false);
              onSaved();
            }}
          />
        ) : (
          <CreateForm
            availablePages={availablePages}
            onSaved={() => {
              onOpenChange(false);
              onSaved();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PermissionsCheckboxes({
  availablePages,
  value,
  onChange,
  disabled = false,
}: {
  availablePages: PageInfo[];
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}): React.ReactElement {
  const toggle = (name: string, checked: boolean) => {
    if (checked) onChange([...value, name]);
    else onChange(value.filter((v) => v !== name));
  };
  return (
    <div className="space-y-2">
      <Label>Доступные страницы</Label>
      {availablePages.length === 0 && (
        <p className="text-sm text-muted-foreground">Нет дополнительных страниц</p>
      )}
      {availablePages.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <Checkbox
            id={`perm_${p.name}`}
            checked={value.includes(p.name)}
            onCheckedChange={(c) => toggle(p.name, c === true)}
            disabled={disabled}
          />
          <Label htmlFor={`perm_${p.name}`} className="cursor-pointer font-normal">
            {p.title}
          </Label>
        </div>
      ))}
    </div>
  );
}

function CreateForm({
  availablePages,
  onSaved,
}: {
  availablePages: PageInfo[];
  onSaved: () => void;
}): React.ReactElement {
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { username: "", password: "", is_admin: false, permissions: [] },
  });

  const onSubmit = async (values: CreateValues) => {
    try {
      await createUser(values);
      toast.success("Пользователь создан");
      onSaved();
    } catch (err) {
      if (err instanceof AxiosError) {
        setError("root", { message: err.response?.data?.detail ?? "Ошибка" });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="c_username">Логин</Label>
        <Input id="c_username" {...register("username")} />
        {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="c_password">Пароль</Label>
        <Input id="c_password" type="password" {...register("password")} />
        {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
      </div>
      <Controller
        control={control}
        name="is_admin"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="c_is_admin"
              checked={field.value}
              onCheckedChange={(c) => field.onChange(c === true)}
            />
            <Label htmlFor="c_is_admin" className="cursor-pointer font-normal">
              Администратор (доступ ко всем разделам, кроме отмеченных отдельным правом
              — напр. «Финплан»)
            </Label>
          </div>
        )}
      />
      <Controller
        control={control}
        name="permissions"
        render={({ field }) => (
          <PermissionsCheckboxes
            availablePages={availablePages}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      {errors.root && <p className="text-sm text-destructive">{errors.root.message}</p>}
      <DialogFooter>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Сохранение…" : "Создать"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function EditForm({
  userId,
  availablePages,
  onSaved,
}: {
  userId: number;
  availablePages: PageInfo[];
  onSaved: () => void;
}): React.ReactElement {
  const [loading, setLoading] = React.useState(true);
  const [detail, setDetail] = React.useState<UserDetail | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UpdateValues>({
    resolver: zodResolver(updateSchema),
    defaultValues: { password: "", is_admin: false, is_active: true, permissions: [] },
  });

  useEffect(() => {
    (async () => {
      const u = await getUser(userId);
      setDetail(u);
      reset({
        password: "",
        is_admin: u.is_admin,
        is_active: u.is_active,
        permissions: u.permissions,
      });
      setLoading(false);
    })();
  }, [userId, reset]);

  if (loading || !detail) return <p className="text-sm text-muted-foreground">Загрузка…</p>;

  // Права SSO-связанного пользователя — роли Zitadel: здесь только просмотр
  // (бэкенд на их изменение отвечает 409 `sso_managed`).
  const ssoManaged = detail.sso_managed;

  const onSubmit = async (values: UpdateValues) => {
    const payload: Record<string, unknown> = { is_active: values.is_active };
    // Права SSO-пользователя не шлём вовсе: их источник — Zitadel.
    if (!ssoManaged) {
      payload.is_admin = values.is_admin;
      payload.permissions = values.permissions;
    }
    if (values.password) payload.password = values.password;

    try {
      await updateUser(userId, payload);
      toast.success("Сохранено");
      onSaved();
    } catch (err) {
      if (err instanceof AxiosError) {
        setError("root", { message: err.response?.data?.detail ?? "Ошибка" });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label>Логин</Label>
        <p className="text-sm text-muted-foreground">{detail.username} (нельзя изменить)</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="e_password">Новый пароль (оставьте пустым, чтобы не менять)</Label>
        <Input id="e_password" type="password" {...register("password")} />
        {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
      </div>
      {ssoManaged && (
        <p className="text-xs text-muted-foreground" data-testid="sso-managed-note">
          Права управляются в SSO (Zitadel)
        </p>
      )}
      <Controller
        control={control}
        name="is_admin"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="e_is_admin"
              checked={field.value}
              onCheckedChange={(c) => field.onChange(c === true)}
              disabled={ssoManaged}
            />
            <Label htmlFor="e_is_admin" className="cursor-pointer font-normal">
              Администратор
            </Label>
          </div>
        )}
      />
      <Controller
        control={control}
        name="is_active"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="e_is_active"
              checked={field.value}
              onCheckedChange={(c) => field.onChange(c === true)}
            />
            <Label htmlFor="e_is_active" className="cursor-pointer font-normal">
              Активен
            </Label>
          </div>
        )}
      />
      <Controller
        control={control}
        name="permissions"
        render={({ field }) => (
          <PermissionsCheckboxes
            availablePages={availablePages}
            value={field.value}
            onChange={field.onChange}
            disabled={ssoManaged}
          />
        )}
      />
      {errors.root && <p className="text-sm text-destructive">{errors.root.message}</p>}
      <DialogFooter>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Сохранение…" : "Сохранить"}
        </Button>
      </DialogFooter>
    </form>
  );
}
